import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPriceMatch, { ProductPriceMatchStyles } from "@preferredMedical/Widgets/ProductDetails/ProductPriceMatch";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    priceMatchDescription = "priceMatchDescription",
    consentDescription = "consentDescription",
    uploadDescription = "uploadDescription",
    emailRecipients = "emailRecipients",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
    };
}

type Props = WidgetProps & OwnProps & HasProduct & ReturnType<typeof mapStateToProps>;
const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);

const mapStateToProps = (state: ApplicationState) => ({
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    session: state.context.session,
});

export interface ProductDetailsPriceMatchButtonStyles {
    wrapper?: InjectableCss;
    priceMatch?: ProductPriceMatchStyles;
}

export const priceMatchStyles: ProductDetailsPriceMatchButtonStyles = {
    wrapper: {
        css: css`
            margin-top: 30px;
            text-align: center;
        `,
    },
};

const styles = priceMatchStyles;

const ProductPriceMatchButton: React.FC<Props> = ({ product, session, fields }) => {
    const { emailRecipients, priceMatchDescription, consentDescription, uploadDescription, title } = fields;
    if (!product) {
        return null;
    }
    if (!session.isAuthenticated) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductPriceMatch
                extendedStyles={styles.priceMatch}
                emailRecipients={emailRecipients}
                consentDescription={consentDescription}
                uploadDescription={uploadDescription}
                priceMatchDescription={priceMatchDescription}
                modalTitle={title}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductPriceMatchButton)),
    definition: {
        displayName: "Price Match",
        group: "Product Details",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients (one on each row)",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.title,
                displayName: "Popup Title",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "PRICE MATCH GUARANTEED",
                isRequired: true,
            },
            {
                name: fields.priceMatchDescription,
                displayName: "Price Match Description",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue:
                    "If you find a lower price on any product we stock, we will match our direct competitors’ prices on identical items that meet our" +
                    " price match conditions. We owe our success to you, our customers, and we know that price is a critical concern. With our price match" +
                    " guarantee, we assure you that by choosing Wolf Medical Supply you're getting the best value.",
                isRequired: true,
            },
            {
                name: fields.consentDescription,
                displayName: "Consent Description",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "I Confirm This Is A Legitimate Price Offering",
                isRequired: true,
            },
            {
                name: fields.uploadDescription,
                displayName: "Upload Description",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue:
                    "Please only upload files in the following formats: JPG, PNG, GIF, BMP or PDF. No other file types will be accepted including" +
                    " Word, Excel or Powerpoint.",
                isRequired: true,
            },
        ],
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
